import { useEffect, useState } from "react"
import { Box, HStack, IconButton, Separator, Spinner, Stack } from "@chakra-ui/react"
import { LuRefreshCw } from "react-icons/lu"
import { IoDownloadOutline } from "react-icons/io5"
import { OfferComponent } from "./components"
import { Button, Section, Text } from "@/components"
import { useDropboxDocuments } from "@/hooks"
import { Deal, Offer } from "@/types"
import { ALL_STEPS, SCHEDULE_CLOSING_MEETING_STEP_INDEX } from "@/constants"

const DEAL_DOCUMENTS = [
  { name: "ESO RSU Tax Memo", file: "ESO RSU Tax Memo.pdf", rsuOnly: true },
  { name: "ESO RSU Tax Preamble", file: "ESO RSU Tax Preamble.pdf", rsuOnly: true },
  { name: "ESO Tax Quickstart", file: "ESO Tax Quickstart.pdf", rsuOnly: false },
  { name: "83i Tax Primer", file: "83i Tax Primer.pdf", rsuOnly: false },
  { name: "CPA Referral List", file: "CPA Referral List.pdf", rsuOnly: false },
]

interface Props {
  isLoading: boolean
  deals: Deal[]
  onRefresh?: () => void
  currentDeal: Deal | null
  onDealChange: (deal: Deal) => void
}

export const DealsComponent = ({ deals, isLoading, onRefresh, currentDeal, onDealChange }: Props) => {
  const [currentOffer, setCurrentOffer] = useState<Offer | null>(null)
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0)
  const isDealInactive = currentDeal?.isActive === 0
  const isReadOnly = isDealInactive

  const { data: dropboxDocuments, isLoading: isLoadingDocs } = useDropboxDocuments(currentDeal?.id ?? undefined);

  const hasEquityUploaded = dropboxDocuments.some(
    (doc) => doc.name.toLowerCase().startsWith("equity holdings")
  );

  const ndaInDropbox = dropboxDocuments.some(
    (doc) => doc.name.toLowerCase().includes("eso mutual nda")
  );

  // After an acceptance the other active offers stay visible, read-only, until the
  // closing meeting step; from then on only the accepted offer shows. Only the DM
  // can change which offer is accepted (the backend refuses a client switch).
  // Bill, 2026-10-12. The step index is computed here, not read from state, so the
  // first render after a deal loads doesn't use a stale step.
  const acceptedOffer = currentDeal?.offers?.find(o => o.isAccepted === 1)
  const dealStepIndex = currentDeal ? ALL_STEPS.findIndex(step => step.step === currentDeal.currentStep) : -1
  const showAllOffers = !acceptedOffer || dealStepIndex < SCHEDULE_CLOSING_MEETING_STEP_INDEX
  const visibleOffers = currentDeal?.offers
    ? (showAllOffers || !acceptedOffer ? currentDeal.offers : [acceptedOffer])
    : []

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.currentStep);
      setCurrentStepIndex(index !== -1 ? index : 0);

      if (visibleOffers.length > 0) {
        setCurrentOffer(acceptedOffer ?? visibleOffers[0])
      } else {
        setCurrentOffer(null)
      }
    }
  }, [currentDeal])

  return (
    <Section>
      <HStack>
        <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
          Your Deals
        </Text>
        {onRefresh && <IconButton aria-label="Refresh" variant="ghost" size="sm" onClick={onRefresh}><LuRefreshCw /></IconButton>}
      </HStack>
      {
        isLoading ? (
          <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
            <Spinner />
          </HStack>
        ) : (
          <>
            {
              currentDeal &&
              !isDealInactive &&
              hasEquityUploaded &&
              (!currentDeal.offers || currentDeal.offers.length === 0) && (
                <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={3} mt={4}>
                  <Text fontSize="sm" color="blue.700">
                    We're currently working on your deal. You'll be notified when your offer is ready.
                  </Text>
                </Box>
              )
            }
            <HStack mt={5} gap={2}>
              {
                deals && deals.length === 1 ? (
                  <HStack>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600" borderWidth={1} borderColor="gray.200" borderRadius="md" px={4} py={2}>
                      {deals[0].company.name}
                    </Text>
                    {deals[0].isActive === 0 && (
                      <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
                      </Box>
                    )}
                  </HStack>
                ) : (
                  deals && deals.map((deal, index) => (
                    <HStack key={index} gap={1}>
                      <Button
                        variant={ deal.id === currentDeal?.id ? "solid" : "outline"}
                        onClick={() => onDealChange(deal)}
                        opacity={deal.isActive === 0 ? 0.6 : 1}
                      >
                        {deal.company.name}
                      </Button>
                      {deal.isActive === 0 && (
                        <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                          <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
                        </Box>
                      )}
                    </HStack>
                  ))
                )
              }
            </HStack>
            {isReadOnly ? (
              <Box bg="gray.100" border="1px solid" borderColor="gray.300" borderRadius="md" px={4} py={3} mt={5}>
                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                  You do not have an active deal in this company, please reach out to your Equity Partner if you have any questions.
                </Text>
              </Box>
            ) : visibleOffers.length > 0 && !ndaInDropbox ? (
              isLoadingDocs ? (
                <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
                  <Spinner />
                </HStack>
              ) : (
                <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={3} mt={5}>
                  <Text fontSize="sm" color="blue.700">
                    You still need to sign the NDA DocuSign email sent to your email address. Please check your email for a DocuSign link sent from docusign@esofund.com.
                  </Text>
                  <Text fontSize="sm" color="blue.700" mt={2}>
                    If you didn't receive an email please contact your Equity Partner.
                  </Text>
                  <Text fontSize="sm" color="blue.700" mt={2}>
                    In the meantime, you can go to the documents page to upload any files you still need to upload. If you have any questions, feel free to email or schedule a call with your equity partner!
                  </Text>
                </Box>
              )
            ) : (
              <>
                {
                  deals && currentDeal && visibleOffers.length > 1 ? (
                    <HStack mt={10}>
                      {visibleOffers.map((offer, index) => (
                        <Button
                          key={index}
                          variant={ offer.id === currentOffer?.id ? "tabSelected" : "tabUnselected"}
                          onClick={() => setCurrentOffer(offer)}
                        >
                          {`Offer ${index + 1}${offer.isAccepted === 1 ? " (Accepted)" : ""}`}
                        </Button>
                      ))}
                    </HStack>
                  ) : !visibleOffers.length ? (
                    <HStack mt={10}><Text>No offers available</Text></HStack>
                  ) : null
                }
                <Separator mt={5} />
                <Stack pl={10} pr={10} pb={5} pt={5}>
                  <Text fontSize="md" fontWeight="bold" pb={3}>Tax Primer</Text>
                  <HStack flexWrap="wrap" gap={3}>
                    {DEAL_DOCUMENTS.filter(doc => currentDeal?.dealType === "RSU" ? true : !doc.rsuOnly).map((doc) => (
                      <Button
                        key={doc.name}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_SERVER_URL}/tax-primers/${doc.file}`, "_blank")
                        }}
                      >
                        <IoDownloadOutline />
                        {doc.name}
                      </Button>
                    ))}
                  </HStack>
                </Stack>
                <Separator />
                <Stack>
                  {
                    currentOffer && currentDeal && (
                      <OfferComponent dealId={currentDeal.id} offer={currentOffer} currentStepIndex={currentStepIndex} dealType={currentDeal.dealType} isReadOnly={isReadOnly} hasAcceptedOffer={!!acceptedOffer} />
                    )
                  }
                </Stack>
                <Text fontSize="xs" color="gray.400" fontStyle="italic" px={10} pb={5} pt={3}>
                  Please note that this page does not constitute a formal offer and only the signed contract between you and ESO Fund will constitute a binding agreement. The amounts shown are for illustrative purposes only and actual proceeds will vary. ESO Fund does not provide legal, financial, or tax advice and nothing on this page should be construed as such.
                </Text>
              </>
            )}
          </>
        )
      }
    </Section>
  )
}
